/**
 * Validation schemas shared by the web client, the future Expo client, and the
 * Worker. Defining them once guarantees client and server agree — the server
 * still validates independently (never trust the client).
 *
 * Password policy follows NIST 800-63B (SECURITY.md §2.1): length over
 * composition rules, generous maximum, common-password blocklist.
 */
import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Caps on free-text fields — bounds D1 row size and abuse (SECURITY.md §5). */
export const LIMITS = {
  name: 100,
  email: 254, // RFC 5321 practical maximum
  ideaTitle: 200,
  ideaSummary: 2000,
  /**
   * One interview or refinement answer. Every character reaches an LLM prompt,
   * so this bounds inference cost as well as storage.
   */
  interviewAnswer: 4000,
  /** One annotation note. Never reaches a model — storage only. */
  feedbackNote: 2000,
  /**
   * Notes per submission. The overlay locks scrolling, so this bounds what
   * one viewport can plausibly hold — well above real use, low enough that a
   * hand-rolled request cannot write thousands of rows in one call.
   */
  feedbackNotesPerSubmission: 20,
} as const;

/**
 * Small blocklist of passwords that pass a length check but are trivially
 * guessable. Not a substitute for the breached-password (HIBP k-anonymity)
 * check planned post-launch — see SECURITY.md §2.1.
 */
const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  'passw0rd',
  'password1234',
  '123456789012',
  '1234567890123',
  'qwertyuiop',
  'qwerty123456',
  'letmein12345',
  'iloveyou1234',
  'administrator',
  'welcome12345',
  'monkey123456',
  'football1234',
  'baseball1234',
  'trustno1',
  'changeme1234',
  'ideabrief123',
]);

function isCommonPassword(value: string): boolean {
  const normalized = value.toLowerCase().replace(/\s+/g, '');
  if (COMMON_PASSWORDS.has(normalized)) return true;
  // A single repeated character, e.g. "aaaaaaaaaaaa".
  if (/^(.)\1+$/.test(normalized)) return true;
  // Straight ascending/descending runs, e.g. "123456789012".
  if (/^(0123456789|abcdefghijklmnopqrstuvwxyz)+/.test(normalized)) return true;
  return false;
}

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Enter your email address to continue.')
  .max(LIMITS.email, 'That email address is too long.')
  .toLowerCase()
  .pipe(
    z.email(
      "That doesn't look like a working email address — check the spelling.",
    ),
  );

export const passwordSchema = z
  .string()
  .min(
    PASSWORD_MIN_LENGTH,
    `Use at least ${PASSWORD_MIN_LENGTH} characters — length matters more than symbols.`,
  )
  .max(PASSWORD_MAX_LENGTH, 'That password is too long.')
  .refine((v) => !isCommonPassword(v), {
    message: 'That password is too easy to guess — pick something less common.',
  });

export const nameSchema = z
  .string()
  .trim()
  .min(1, 'Enter your name.')
  .max(LIMITS.name, 'That name is too long.');

/*
 * THERE IS NO signUpSchema, AND ITS ABSENCE IS THE POINT.
 *
 * One used to live here, and for months three code comments cited it as the
 * place consent was enforced. It was imported nowhere: Better Auth owns
 * /api/auth/sign-up/email and validates that body itself, so this object was
 * never on the request path. The real gap was that nothing checked consent at
 * all — a direct POST created an account, and the durable consent row then
 * recorded `granted: 1` for an agreement the server had never received.
 *
 * Consent is now enforced where the request actually passes: the /api/auth/*
 * guard in apps/worker/src/index.ts rejects a sign-up whose body lacks a
 * literal `consent: true`, before Better Auth runs. See `hasAffirmativeConsent`
 * and SECURITY.md §5.
 *
 * If you are tempted to re-add a schema here to "share sign-up validation":
 * it will not run unless something imports it, and a schema that merely exists
 * is worse than none — it reads as a control while enforcing nothing. The
 * FIELD-level schemas below (email, password, name) are the shared part, and
 * the client forms do use them.
 */

export const signInSchema = z.object({
  email: emailSchema,
  /**
   * Deliberately only checked for presence, not policy. Applying the full
   * policy here would let an attacker infer which passwords could exist.
   */
  password: z.string().min(1, 'Enter your password.'),
  captchaToken: z.string().min(1, 'Captcha verification failed — try again.'),
});

export const requestPasswordResetSchema = z.object({
  email: emailSchema,
  captchaToken: z.string().min(1, 'Captcha verification failed — try again.'),
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1),
  password: passwordSchema,
});

export const createIdeaSchema = z.object({
  title: z.string().trim().min(1, 'Give your idea a title.').max(LIMITS.ideaTitle),
  summary: z.string().trim().max(LIMITS.ideaSummary).optional(),
});

/**
 * Scan submission — the anonymous interview being claimed by a signed-in user.
 *
 * Length caps are not cosmetic: every character here ends up in an LLM prompt,
 * so an uncapped field is a cost vector as much as a storage one. The interview
 * textarea is a few sentences in practice; 4000 is generous for that while
 * bounding the worst case.
 */
export const scanAnswerSchema = z.object({
  questionId: z.string().min(1).max(32),
  answer: z.string().trim().max(LIMITS.interviewAnswer),
  skipped: z.boolean(),
});

export const scanRequestSchema = z.object({
  /** Section overrides from the launch screen's inline editing. */
  brief: z.record(z.string().max(32), z.string().trim().max(LIMITS.interviewAnswer))
    .optional(),
  answers: z
    .array(scanAnswerSchema)
    // Exactly the 8 static questions; more means a malformed or hostile client.
    .min(1, 'Answer at least one question before scanning.')
    .max(8),
});

/** One refinement answer, or a pass. */
export const refinementAnswerSchema = z.object({
  queryId: z.string().min(1).max(64),
  answer: z.string().trim().max(LIMITS.interviewAnswer),
  /** True routes the topic to the research agents with an empty answer. */
  passed: z.boolean(),
});

/**
 * THE RESEARCH HANDOFF CONTRACT.
 *
 * The research pipeline does not write to this application. It produces a
 * manifest and hands it over; the app validates it, stores the deliverables,
 * writes its own records, emails the user, and marks delivery. That boundary is
 * the point: a pipeline that reaches into D1 directly would bypass ownership
 * checks, skip the email, and leave records only it knows how to repair.
 *
 * Everything the pipeline asserts is DATA to be checked, never instructions.
 * The counts below are the clearest case — `sourcesTotal` and `sourcesCited`
 * become the "BUILT FROM n SOURCES" line the product shows a user, so the app
 * treats them as claims to be range-checked, not as facts to be printed.
 *
 * WHY VERDICT IS AN ENUM: it colours the dashboard banner. A free-text verdict
 * would render an uncoloured chip rather than failing, so the boundary rejects
 * an unrecognised value instead of the UI degrading silently.
 */
export const reportMoveSchema = z.object({
  label: z.string().trim().min(1).max(120),
  fromScore: z.number().min(0).max(10),
  toScore: z.number().min(0).max(10),
  note: z.string().trim().min(1).max(1000),
});

export const reportDeliverableSchema = z.object({
  kind: z.enum(['deck', 'detailed', 'verdict']),
  /**
   * R2 object key. Constrained to the reports/ prefix so a manifest can never
   * point the download endpoint at an arbitrary object in the bucket, and
   * dot-segments are rejected outright rather than normalised.
   */
  r2Key: z
    .string()
    .min(1)
    .max(256)
    .regex(/^reports\/[A-Za-z0-9][A-Za-z0-9/_-]*\.pdf$/, 'Key must be reports/<path>.pdf')
    .refine((k) => !k.includes('..'), 'Key must not contain dot-segments'),
  /** Bytes, as uploaded. The app verifies the object matches before delivering. */
  bytes: z.number().int().positive().max(50 * 1024 * 1024),
});

export const reportManifestSchema = z.object({
  ideaId: z.string().uuid(),
  /** Which run produced this, for audit. Mirrors the run directory name. */
  runId: z.string().trim().min(1).max(64),
  verdict: z.enum(['proceed', 'proceed_with_changes', 'rethink']),
  headline: z.string().trim().min(1).max(200),
  /** Plain text, never HTML — the dashboard renders it as a text node. */
  body: z.string().trim().min(1).max(4000),
  /** Post-research score. May differ from the scan; that is the point of research. */
  score: z.number().min(0).max(10),
  sourcesTotal: z.number().int().min(0).max(10_000),
  sourcesCited: z.number().int().min(0).max(10_000),
  deliverables: z.array(reportDeliverableSchema).min(1).max(3),
  moves: z.array(reportMoveSchema).max(8).optional(),
})
  // A report cannot cite more sources than it gathered. This is the one
  // cross-field claim worth rejecting outright: it would print a false
  // provenance line, which is precisely the promise the product makes.
  .refine((m) => m.sourcesCited <= m.sourcesTotal, {
    message: 'sourcesCited cannot exceed sourcesTotal',
    path: ['sourcesCited'],
  })
  // Two deliverables of the same kind would race for the same dashboard slot.
  .refine((m) => new Set(m.deliverables.map((d) => d.kind)).size === m.deliverables.length, {
    message: 'deliverables must have distinct kinds',
    path: ['deliverables'],
  });

export type ReportMoveInput = z.infer<typeof reportMoveSchema>;
export type ReportDeliverableInput = z.infer<typeof reportDeliverableSchema>;
export type ReportManifestInput = z.infer<typeof reportManifestSchema>;

export type ScanAnswerInput = z.infer<typeof scanAnswerSchema>;
export type ScanRequestInput = z.infer<typeof scanRequestSchema>;
export type RefinementAnswerInput = z.infer<typeof refinementAnswerSchema>;

/**
 * One annotation: where typing started, and what was written.
 *
 * Coordinates are DOCUMENT-space — the position on the page, not on the
 * screen. The page scrolls freely while annotating, so a viewport coordinate
 * would stop meaning anything the moment it moved. `y` is bounded generously
 * because a long page is legitimately tall.
 */
export const annotationNoteSchema = z.object({
  x: z.number().int().min(0).max(20_000),
  y: z.number().int().min(0).max(1_000_000),
  text: z
    .string()
    .trim()
    .min(1)
    .max(LIMITS.feedbackNote, 'That note is too long — trim it a little.'),
});

/**
 * A Save: one screen, N notes.
 *
 * NOTE WHAT IS ABSENT: there is no `userId` field, and adding one would be a
 * vulnerability rather than a convenience. The Worker takes the author from
 * `session.user.id`; a client-supplied id would let any signed-in user file
 * feedback as somebody else.
 *
 * Unlike the deleted `signUpSchema`, this one IS on the request path — the
 * feedback route parses with it directly. A schema that merely exists reads as
 * a control while enforcing nothing (see the comment where signUpSchema used
 * to be), so this must stay wired into the route it claims to guard.
 */
export const annotationSubmissionSchema = z.object({
  /** Route path only — never the query string. */
  path: z.string().trim().min(1).max(200),
  /** Human name for the screen, resolved by the client from the route. */
  screen: z.string().trim().min(1).max(80),
  viewportW: z.number().int().positive().max(20_000),
  viewportH: z.number().int().positive().max(20_000),
  /**
   * Where the page was scrolled when the overlay opened. Note coordinates are
   * document-space and do not need this to be located — it records which part
   * of the page the user was looking at when they started.
   */
  scrollY: z.number().int().min(0).max(1_000_000),
  notes: z
    .array(annotationNoteSchema)
    .min(1, 'Add a note before saving.')
    .max(
      LIMITS.feedbackNotesPerSubmission,
      'That is more notes than we can take at once.',
    ),
});

export type AnnotationNoteInput = z.infer<typeof annotationNoteSchema>;
export type AnnotationSubmissionInput = z.infer<typeof annotationSubmissionSchema>;

export type SignInInput = z.infer<typeof signInSchema>;
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type CreateIdeaInput = z.infer<typeof createIdeaSchema>;

/**
 * Uniform failure copy for sign-in. Never distinguish "no such account" from
 * "wrong password" — that difference is a user-enumeration oracle
 * (SECURITY.md §2.4).
 */
export const GENERIC_AUTH_FAILURE =
  "That email and password don't match. Check them and try again.";

/**
 * Uniform response for password-reset requests, shown whether or not the
 * account exists (SECURITY.md §2.3).
 */
export const RESET_REQUESTED_MESSAGE =
  "If that account exists, we've emailed a reset link. It expires in an hour.";
